import React, { useEffect, useState, useContext } from 'react';
import { useSelector } from 'react-redux';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import Paper from '@mui/material/Paper';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import CryptoJS from 'crypto-js';
import { useTranslation } from 'react-i18next';
import styles from './Qr.module.scss';
import { selectIsAuth } from '../../redux/slices/auth';
import egov1 from "../../img/egov1.png";
import egov2 from "../../img/egov2.png";
import egov3 from "../../img/egov3.png";
import axios from 'axios';
import { IinContext } from '../../context/IinContext';
import { AuthTabs, PasswordForm, EmailConfirmation, VerificationCodeInput } from '../../components';
import { getCodeErrorMessage } from '../../utils/verificationCode';
import Cookies from 'js-cookie';

export const Qr = () => {
  const { t } = useTranslation();
  const isAuth = useSelector(selectIsAuth);
  const [guid, setGuid] = useState('');
  const [qrImageUrl, setQrImageUrl] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordInput, setShowPasswordInput] = useState(false);
  const [showCodeInput, setShowCodeInput] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [showEmailConfirmation, setShowEmailConfirmation] = useState(false);
  const [email, setEmail] = useState('');
  const location = useLocation();
  const navigate = useNavigate();
  const { setIin, setIinOrBin, setUserInfo, setIinForBin, setFirstName, setLastName, setPhysPersonFullname, setBin, setOrgName } = useContext(IinContext);
  const [activeTab, setActiveTab] = useState('/qr');
  const [verificationCode, setVerificationCode] = useState('');
  const [showLogin, setShowLogin] = useState(false);
  const [showResendButton, setShowResendButton] = useState(false);
  const [showEmailInput, setShowEmailInput] = useState(false);

  useEffect(() => {
    const fetchGuidAndQrImage = async () => {
      try {
        const { data: guidData } = await axios.get('/kcssobackext/qr-auth/guid');
        const trimmedGuid = guidData.trim();
        setGuid(trimmedGuid);

        const qrResponse = await axios.get(`/kcssobackext/qr-auth?guid=${trimmedGuid}`, {
          responseType: 'blob',
        });
        const qrBlob = qrResponse.data;
        const qrImageUrl = URL.createObjectURL(qrBlob);
        setQrImageUrl(qrImageUrl);
      } catch (error) {
        console.error('Error fetching GUID or QR Image:', error);
      }
    };
  
    fetchGuidAndQrImage();
  }, []);

  useEffect(() => {
    const validateExistingSecretCode = async () => {
      const existingSecretCode = Cookies.get('secretCode');
      if (existingSecretCode) {
        try {
          const response = await axios.post('/kcssobackext/sso/external/validateSecretCode', {
            secretCode: existingSecretCode
          });
          
          if (response.data === true) {
            await redirectToReferrer(existingSecretCode);
          } else {
            Cookies.remove('secretCode');
          }
        } catch (error) {
          console.error("Ошибка при валидации secretCode:", error);
          Cookies.remove('secretCode');
        }
      }
    };

    validateExistingSecretCode();
  }, []);

  useEffect(() => {
    let intervalId;

    if (guid) {
      const checkQrStatus = async () => {
        try {
          const { data: status } = await axios.get(`/kcssobackext/qr-auth/status/${guid}`);
          if (status) {
            const { data: qrInfo } = await axios.get(`/kcssobackext/qr-auth/getInfoFromQR/${guid}`);
            const { iin, bin, fullName, organization, firstName, lastName } = qrInfo;

            if (!bin) {
              setIin(iin);
              setIinOrBin(iin);
              setIinForBin(iin);
              setFirstName(firstName);
              setLastName(lastName);
              setPhysPersonFullname(fullName);
              sessionStorage.setItem('iinBin', iin);
              sessionStorage.setItem('name', fullName);
              sessionStorage.setItem('physPerson', true);
            } else {
              setBin(bin);
              setIinOrBin(bin);
              setOrgName(organization);
              setFirstName(firstName);
              setLastName(lastName);
              setPhysPersonFullname(fullName);
              sessionStorage.setItem('iinBin', bin);
              sessionStorage.setItem('name', organization);
              sessionStorage.setItem('physPerson', false);
            }

            const iinBin = sessionStorage.getItem('iinBin');
            const { data: userData } = await axios.get(`/kcssobackext/sso/getInfo?iinOrBin=${iinBin}`);
            const { email, exists, physPerson, emailVerified } = userData;

            if (email) {
              sessionStorage.setItem('email', email);
            }  

            if (exists) {
              if (emailVerified) {
                setShowPasswordInput(true);
                setShowLogin(true);
              } else {
                setEmail(email);
                setShowEmailConfirmation(true);
              }
            } else {
              navigate('/register');
            }

            clearInterval(intervalId);
          }
        } catch (error) {
          console.error('Error during QR status check or data retrieval:', error);
        }
      };

      intervalId = setInterval(checkQrStatus, 5000);
    }

    return () => clearInterval(intervalId);
  }, [guid, navigate]);

  if (isAuth) {
    return <Navigate to="/" />;
  }

  const handleEmailSubmit = async () => {
    if (!email) return;

    setShowResendButton(true);

    const iinBin = sessionStorage.getItem('iinBin');

    try {
      const response = await axios.post('/kcssobackext/sso/external/changeEmail', {
        iinOrBin: iinBin,
        email: email,
      });

      if (response.status === 200) {
        setShowCodeInput(true);
        setShowEmailConfirmation(false);
        setShowResendButton(false);
      } else {
        alert(t('error.sendCodeError'));
      }
    } catch (error) {
      console.error(t('error.sendCodeError'), error);
      alert(t('error.sendCodeError'));
    }
  };

  const handleChangeEmail = () => {
    setShowEmailConfirmation(false);
    setEmail('');
    setErrorMessage('');
    setShowEmailInput(true);
  };

  const handleCodeSubmit = async (code) => {
    setVerificationCode(code);

    const iinBin = sessionStorage.getItem('iinBin');
    
    try {
      const response = await axios.post('/kcssobackext/sso/external/checkVerificationCode', {
        iinOrBin: iinBin,
        code: code,
        method: "email"
      });
    
      if (response.data == true) {
        setShowCodeInput(false);
        setShowLogin(true);
        setShowResendButton(false);
        setShowEmailConfirmation(false);
        setShowEmailInput(false);
        setLoading(false);
      } else {
        alert(t('error.invalidCode'));
        setShowResendButton(true);
      }
    } catch (error) {
      if (error.response) {
        const codeErrorMessage = getCodeErrorMessage(error.response.status, error.response.data, t);
        if (codeErrorMessage) {
          alert(codeErrorMessage);
          setShowResendButton(true);
        } else {
          console.error(t('error.checkCodeError'), error);
          alert(t('error.checkCodeError'));
        }
      }
    }
  };

  const handlePasswordSubmit = async (password) => {
    setLoading(true);
    setErrorMessage('');
    try {
      const existingSecretCode = Cookies.get('secretCode');
      if (existingSecretCode) {
        try {
          const validationResponse = await axios.post('/kcssobackext/sso/external/validateSecretCode', {
            secretCode: existingSecretCode
          });
          
          if (validationResponse.data === true) {
            await redirectToReferrer(existingSecretCode);
            setLoading(false);
            return;
          } else {
            Cookies.remove('secretCode');
          }
        } catch (error) {
          console.error("Ошибка при валидации secretCode:", error);
          Cookies.remove('secretCode');
        }
      }

      const key = CryptoJS.enc.Hex.parse('10c3417e0b38987f3b44528d999a090efd708bcef15d6357c252bcd42b4cf03d');
      const iv = CryptoJS.enc.Hex.parse('315f48391c3d3cc857128287f8457d47');
      const encryptedPassword = CryptoJS.AES.encrypt(password, key, { iv: iv }).toString();

      const clientId = sessionStorage.getItem('client-id');

      const response = await axios.post('/kcssobackext/sso/external/qr/login', {
        guid,
        password: encryptedPassword,
        clientId: Number(clientId)
      });

      if (response.status === 401) {
        setErrorMessage(t('qr.invalidPassword'));
        return;
      }

      if (response.status === 500) {
        const errorText = response.data;
        if (errorText.includes('401 Unauthorized')) {
          setErrorMessage(t('qr.invalidPassword'));
        } else {
          setErrorMessage(t('qr.serverError'));
        }
        return;
      }

      if (response.status !== 200) {
        throw new Error('Network response was not ok');
      }

      if (response.data && response.data) {
        Cookies.set('secretCode', response.data, { 
          expires: 0.25,
          domain: window.location.hostname,
          path: '/kclssofront',
          sameSite: 'Lax',
          secure: false 
        });
        await redirectToReferrer(response.data);
      } else {
        throw new Error('Secret code not found in response');
      }

    } catch (error) {
      console.error('Error during login:', error);
      setErrorMessage(t('qr.loginError'));
    } finally {
      setLoading(false);
    }
  };

  const fetchRedirectUrl = async (clientId) => {
    try {
      const response = await axios.get(`/kcssobackext/client/${clientId}`);
      if (response.data && response.data.redirectUrl) {
        return response.data.redirectUrl;
      } else {
        throw new Error("Redirect URL not found in response");
      }
    } catch (error) {
      console.error("Error fetching redirect URL:", error);
      throw error;
    }
  };

  
  const sendPostMessage = (secretCode, backurl) => {
    if (window.opener) {
      window.opener.postMessage(
        { 
          type: 'AUTH_SECRET_CODE', 
          secretCode: secretCode,
          backurl: backurl
        },
        "*"
      );
      window.close();
    }
  };

  const redirectToReferrer = async (secretCode) => {
    const clientId = sessionStorage.getItem('client-id');
    const backurl = sessionStorage.getItem('backurl');
    
    if (!clientId) {
      console.error("Client ID is missing");
      return;
    }
  
    try {
      const validationResponse = await axios.post('/kcssobackext/sso/external/validateSecretCode', {
        secretCode: secretCode
      });
      
      if (validationResponse.data !== true) {
        Cookies.remove('secretCode');
        return;
      }

      if (window.opener) {
        sendPostMessage(secretCode, backurl);
        return;
      }

      const redirectUrl = await fetchRedirectUrl(clientId);
  
      const url = new URL(redirectUrl);
      url.searchParams.set('client-id', clientId);
      url.searchParams.set('secret-code', secretCode);
    } catch (error) {
      console.error("Error during redirection:", error);
      navigate('/');
    }
  };

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handleEmailKeyDown = (e) => {
    if (e.key === 'Enter' && email) {
      handleEmailSubmit();
    }
  };

  const handlePasswordKeyDown = (e) => {
    if (e.key === 'Enter' && password) {
      handlePasswordSubmit(password);
    }
  };

  const isQrPage = location.pathname === '/qr';

  return (
    <div>
      <Paper classes={{ root: styles.root }}>
        <div className={styles.tab}>
          <AuthTabs activeTab={activeTab} setActiveTab={setActiveTab} />
        </div>
        {!showPasswordInput && !showEmailConfirmation && !showEmailInput && !showCodeInput && !showLogin ? (
          <div className={styles.qrContainer}>
            <p>{t('qr.instruction1')}</p>
            <p style={{ color: "#406ABF" }}>{t('qr.instruction2')}</p>
            {qrImageUrl ? (
              <div className={styles.qrCode}>
                <img src={qrImageUrl} alt={t('qr.qrCodeAlt')} />
              </div>
            ) : (
              <p>{t('qr.loadingQrCode')}</p>
            )}
            <ul style={{ display: "flex", justifyContent: "space-around", listStyle: "none", padding: 0, width: "100%" }}>
              <li className={styles.stepItem} style={{ width: "33.333%", textAlign: "center" }}>
                <img className={styles.stepImg} src={egov1} alt=""/>
                <p>{t('qr.step1')}</p>
              </li>
              <li className={styles.stepItem} style={{ width: "33.333%", textAlign: "center" }}>
                <img className={styles.stepImg} src={egov2} alt=""/>
                <p>{t('qr.step2')}</p>
              </li>
              <li className={styles.stepItem} style={{ width: "33.333%", textAlign: "center" }}>
                <img className={styles.stepImg} src={egov3} alt=""/>
                <p>{t('qr.step3')}</p>
              </li>
            </ul>
          </div>
        ) : showEmailConfirmation && !showLogin && !showEmailInput ? (
          <EmailConfirmation
            email={email}
            onConfirm={handleEmailSubmit}
            onChangeEmail={handleChangeEmail}
          />
        ) : showLogin && !showEmailInput ? (
          <div className={styles.qrContainer}>
            <p>{t('qr.enterPassword')}</p>
            {errorMessage && <p style={{ color: 'red' }}>{errorMessage}</p>}
            <div className={styles.passwordContainer}>
              <PasswordForm
                onSubmit={handlePasswordSubmit}
                isLoading={loading}
                errorMessage={errorMessage}
                passwordLabel={t('qr.passwordLabel')}
                submitButtonText={t('qr.login')}
                onKeyDown={handlePasswordKeyDown}
              />
            </div>
          </div>
        ): null}
        {showEmailInput && !showCodeInput && (
          <div className={styles.emailConfirmationContainer}>
            <h2>{t('emailConfirmation.title')}</h2>
            <p>{t('emailConfirmation.enterEmail')}</p>
            <div className={styles.emailConfirmationContent}>
              <TextField
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onKeyDown={handleEmailKeyDown}
                variant="outlined"
                margin="normal"
              />
              <Button
                variant="contained"
                color="primary"
                onClick={handleEmailSubmit}
              >
                {t('emailConfirmation.sendCode')}
              </Button>
            </div>
          </div>
        )}
        {showCodeInput && (
          <div className={styles.emailConfirmationContainer}>
            <VerificationCodeInput
              onSubmit={handleCodeSubmit}
              onResend={handleEmailSubmit}
              showResendButton={showResendButton}
            />
          </div>
        )}
      </Paper>
      <p style={{ fontSize: "12px", textAlign: "center" }}>
        {t('footer.disclaimer1')}
      </p>
      <p style={{ fontSize: "12px", textAlign: "center" }}>
        {t('footer.disclaimer2')}
      </p>
    </div>
  );
};
